import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18next';
import { path, pathOr, is } from 'ramda';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';

import { DisplayFlexS } from '../../../utils/stylesHelpers';
import { getScreenSize, getThemeColor, getThemeSize } from '../../../utils/theme';
import StartGoldWithBg from '../../assets/imgs/star-gold-with-bg.svg';
import StartGoldBg from '../../assets/imgs/star-gold-without-bg.svg';
import StartWithBg from '../../assets/imgs/star-with-bg.svg';
import StarImg from '../../assets/imgs/star-without-bg.svg';
import { Text, Rating, Divider } from '../common';

const TooltipWrap = styled.div`
  position: absolute;
  left: ${({ posLeft }) => posLeft};
  top: ${({ posTop }) => posTop};

  display: inline;

  background-color: ${getThemeColor(['white'])};
  text-transform: initial;
  border-radius: 3px;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.25);
  padding: 15px 10px;
  z-index: 1000;

  width: 195px;
`;

const RatingVotedCountText = styled(Text)`
  line-height: 24px;
  color: ${getThemeColor(['gray_1'])};
  font-size: ${getThemeSize(['normalText'])};

  &:hover {
    color: ${({ clickable }) => clickable && '#A5A5A5'};
    cursor: ${({ clickable }) => clickable && 'pointer'};
  }

  @media (max-width: ${getScreenSize(['medium'])}) {
    font-size: ${getThemeSize(['smallText'])};
    font-weight: 300;
  }
`;

const TextTitleTooltipS = styled(Text)`
  line-height: 18px;
  font-size: ${getThemeSize(['smallText'])};
  font-weight: normal;
  display: block;
  text-align: left;
  margin-bottom: 5px;
`;

const TextReviewTooltipS = styled(TextTitleTooltipS)`
  color: ${getThemeColor(['gray_1'])};
  margin-bottom: 0;
  margin-top: 5px;
  font-weight: normal;
`;

const TextDescTooltipS = styled(TextReviewTooltipS)`
  font-size: ${getThemeSize(['middleSmallText'])};
  line-height: 1.2;
`;

export const CompanyRating = ({
  rating = '-',
  voted = 0,
  iconColor = 'red',
  onRatingCountClick = null,
  hoverHandler = null,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', marginRight: '3px' }}>
    <img
      src={
        rating > 0 ? (iconColor === 'red' ? StartWithBg : StartGoldWithBg) : iconColor === 'red' ? StarImg : StartGoldBg
      }
      alt="star-icon"
    />
    <Text
      weight="300"
      size="bigText"
      lineHeight="24px"
      color={rating === '-' && 'greyDark'}
      style={{ margin: '0px 5px' }}
    >
      {is(Number, rating) ? parseFloat(parseFloat(rating).toFixed(1)) : rating}/5
    </Text>
    <RatingVotedCountText
      onClick={onRatingCountClick}
      clickable={(onRatingCountClick || hoverHandler) && 'clickable'}
      onMouseEnter={hoverHandler}
      onMouseLeave={hoverHandler}
    >
      (
      {rating === '-'
        ? i18n.t('companyItem.ratingComponent.noRating')
        : i18n.t('companyItem.ratingCount', { count: voted })}
      )
    </RatingVotedCountText>
  </div>
);

export const CompanyRatingCombined = ({
  internalRating,
  ratingFromOtherSources,
  ratingFromOthersSum,
  onInternalReviewsCountClick,
  ratingViewCompany,
}) => {
  const [internalRatingTooltip, setInternalRatingTooltip] = useState(false);
  const [otherRatingTooltip, setOtherRatingTooltip] = useState(false);
  const [otherRatingDetailsTooltip, setOtherRatingDetailsTooltip] = useState(false);

  const renderInternalRatingTooltip = () => (
    <TooltipWrap posLeft="90%" posTop="100%">
      <TextTitleTooltipS>
        <Trans i18nKey="companyItem.ratingComponent.tooltip.main" />
        <br />
        <Trans i18nKey="companyItem.ratingComponent.tooltip.averageTreuh" />
      </TextTitleTooltipS>
    </TooltipWrap>
  );

  const renderOtherRatingDetailsTooltip = () => (
    <TooltipWrap posLeft="30%" posTop="100%" style={{ width: '215px', flexDirection: 'column' }}>
      <TextTitleTooltipS>
        <Trans i18nKey="companyItem.ratingComponent.tooltip.main" />
        <br />
        <Trans i18nKey="companyItem.ratingComponent.tooltip.averageGoogle" />
      </TextTitleTooltipS>
      <Rating
        readonly
        showMaxRating
        selectedRating={pathOr('-', ['google', 'average'], ratingFromOtherSources)}
        filledStarImg={StartGoldWithBg}
        emptyStarImg={StartGoldBg}
        styles={{
          widthImg: '17px',
          marginRight: '5px',
          fontSize: 'normalText',
        }}
      />
      <TextReviewTooltipS>
        ({pathOr(0, ['google', 'voted'], ratingFromOtherSources)}
        <Trans i18nKey="companyItem.ratingComponent.tooltip.reviews" />)
      </TextReviewTooltipS>

      <Divider style={{ margin: '10px 0' }} />

      <TextTitleTooltipS>
        <Trans i18nKey="companyItem.ratingComponent.tooltip.main" />
        <br />
        <Trans i18nKey="companyItem.ratingComponent.tooltip.averageTreuhandvergleich" />
      </TextTitleTooltipS>
      <Rating
        readonly
        showMaxRating
        selectedRating={pathOr('-', ['treuhandvergleich', 'average'], ratingFromOtherSources)}
        filledStarImg={StartGoldWithBg}
        emptyStarImg={StartGoldBg}
        styles={{
          widthImg: '17px',
          marginRight: '5px',
          fontSize: 'normalText',
        }}
      />
      <TextReviewTooltipS>
        ({pathOr(0, ['treuhandvergleich', 'voted'], ratingFromOtherSources)}
        <Trans i18nKey="companyItem.ratingComponent.tooltip.reviews" />)
      </TextReviewTooltipS>
      <TextDescTooltipS>
        <Trans i18nKey="companyItem.ratingComponent.tooltip.desc" /> <br />
        <Trans i18nKey="companyItem.ratingComponent.tooltip.descStarSystem" />
      </TextDescTooltipS>
    </TooltipWrap>
  );

  const renderOtherRatingTooltip = () => (
    <TooltipWrap posLeft="90%" posTop="100%">
      <TextTitleTooltipS>
        <Trans i18nKey="companyItem.ratingComponent.tooltip.averagePortal" />
      </TextTitleTooltipS>
    </TooltipWrap>
  );

  return (
    <DisplayFlexS
      direction={ratingViewCompany ? 'row' : 'column'}
      width="auto"
      className="rating-container"
      // Hide ratings until the evaluation system is updated (current system is outdated).
      style={{ display: 'none' }}
    >
      <DisplayFlexS width="auto" aItems="center" style={{ marginBottom: '10px', position: 'relative' }}>
        <CompanyRating
          rating={
            path(['average'], internalRating) === 0 || !path(['average'], internalRating)
              ? '-'
              : path(['average'], internalRating)
          }
          voted={path(['voted'], internalRating)}
          onRatingCountClick={onInternalReviewsCountClick}
        />
        <FontAwesomeIcon
          icon="question-circle"
          color="#C4C4C4"
          onMouseOver={() => setInternalRatingTooltip(true)}
          onMouseOut={() => setInternalRatingTooltip(false)}
        />
        {internalRatingTooltip && renderInternalRatingTooltip()}
      </DisplayFlexS>

      <DisplayFlexS
        width="auto"
        aItems="center"
        className="rating-container"
        style={{ marginLeft: ratingViewCompany && '15px', position: 'relative' }}
      >
        <CompanyRating
          hoverHandler={() => setOtherRatingDetailsTooltip(!otherRatingDetailsTooltip)}
          rating={ratingFromOthersSum === 0 || !ratingFromOthersSum ? '-' : ratingFromOthersSum}
          voted={
            path(['treuhandvergleich', 'voted'], ratingFromOtherSources) +
            path(['google', 'voted'], ratingFromOtherSources)
          }
          iconColor="gold"
        />
        <FontAwesomeIcon
          icon="question-circle"
          color="#C4C4C4"
          onMouseOver={() => setOtherRatingTooltip(true)}
          onMouseOut={() => setOtherRatingTooltip(false)}
        />
        {otherRatingDetailsTooltip && renderOtherRatingDetailsTooltip()}
        {otherRatingTooltip && renderOtherRatingTooltip()}
      </DisplayFlexS>
    </DisplayFlexS>
  );
};
